"use client";

import Link from 'next/link';
import { Calendar, Users, ChartBar, ShieldCheck, CalendarCheck, BellRing, Sparkles, Star, Globe, Smartphone, Gift, CreditCard, Timer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTrialConfig } from '@/hooks/useTrialConfig';
import { useSupportContact, waLink } from '@/hooks/useSupportContact';
import { useIsNativeApp } from '@/lib/platform';

export default function BusinessLanding() {
  const { t } = useTranslation('common');
  // K-0071. This page is reachable from the app's menu, and it carried the two
  // lines that name money: the "N months free" badge and «afterwards 99 ₪ a
  // month». Everything else — what the cabinet does, the registration button —
  // stays: signing up is free and is not a purchase.
  const isNative = useIsNativeApp();
  // The free-trial length shown to masters comes from the admin-configurable
  // value (system_config/trial), so changing the trial in the admin panel never
  // leaves stale marketing copy behind.
  const { trialMonths } = useTrialConfig();
  const { whatsapp } = useSupportContact();
  const OFFER_ITEMS = [
    { icon: CalendarCheck, n: 1 },
    { icon: BellRing, n: 2 },
    { icon: Sparkles, n: 3 },
    { icon: Star, n: 4 },
    { icon: Globe, n: 5 },
    { icon: Smartphone, n: 6 },
  ];

  return (
    <div className="flex flex-col min-h-screen overflow-x-hidden">
      <div className="container mx-auto px-4 mt-4">
      <section className="relative w-full py-12 sm:py-16 md:py-24 bg-linear-to-br from-indigo-900 to-black text-white rounded-3xl overflow-hidden shadow-2xl">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=2000&q=80')] opacity-20 mix-blend-overlay bg-cover bg-center"></div>
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 md:gap-12 px-6 sm:px-10 md:px-12">
          <div className="w-full md:w-1/2 flex flex-col items-start text-left">
            <span className="inline-block py-1 px-3 rounded-full bg-white/10 border border-white/20 text-sm font-medium mb-6">
              {t('business_landing.badge')}
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight mb-6 md:mb-8 leading-tight break-words">
              {t('business_landing.title_1')} <br /> {t('business_landing.title_2')}
            </h1>
            <p className="text-lg text-white/80 mb-10 max-w-lg">
              {t('business_landing.subtitle')}
            </p>
            <Link href="/business/register" className="bg-primary text-primary-foreground font-semibold px-8 py-4 rounded-xl text-lg hover:shadow-lg hover:shadow-primary/30 transition-all hover:-translate-y-1">
              {t('business_landing.btn_start')}
            </Link>
          </div>
          <div className="w-full md:w-1/2 glass-panel p-8 rounded-3xl border-white/20 hidden md:block relative overflow-hidden ring-1 ring-black/5 dark:ring-white/10">
            {/* Background glowing effects */}
            <div className="absolute top-[-50px] right-[-50px] w-[200px] h-[200px] bg-rose-500/20 blur-[80px] rounded-full"></div>
            <div className="absolute bottom-[-50px] left-[-50px] w-[200px] h-[200px] bg-primary/20 blur-[80px] rounded-full"></div>
            
            <h3 className="text-3xl font-black mb-8 relative z-10 text-foreground dark:text-white">
              {t('business_landing.benefit_title', 'Что вы получаете?')}
            </h3>

            <div className="flex flex-col gap-8 relative z-10">
              <div className="flex gap-5 items-start group">
                <div className="w-14 h-14 rounded-2xl bg-background/50 dark:bg-white/10 flex items-center justify-center shrink-0 border border-border dark:border-white/10 group-hover:bg-primary/20 group-hover:border-primary/50 transition-all shadow-lg">
                  <span className="text-2xl">✨</span>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2 text-foreground dark:text-white">{t('business_landing.benefit_1_title', 'Личная страница')}</h4>
                  <p className="text-muted-foreground dark:text-white/70 leading-relaxed text-sm">{t('business_landing.benefit_1_desc', 'Красивый профиль, галерея работ и прайс-лист для привлечения клиентов.')}</p>
                </div>
              </div>

              <div className="flex gap-5 items-start group">
                <div className="w-14 h-14 rounded-2xl bg-background/50 dark:bg-white/10 flex items-center justify-center shrink-0 border border-border dark:border-white/10 group-hover:bg-indigo-500/20 group-hover:border-indigo-500/50 transition-all shadow-lg">
                  <span className="text-2xl">🗓️</span>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2 text-foreground dark:text-white">{t('business_landing.benefit_2_title', 'Умная онлайн-запись')}</h4>
                  <p className="text-muted-foreground dark:text-white/70 leading-relaxed text-sm">{t('business_landing.benefit_2_desc', 'Удобный календарь, автоматические напоминания и защита от неявок.')}</p>
                </div>
              </div>

              <div className="flex gap-5 items-start group">
                <div className="w-14 h-14 rounded-2xl bg-background/50 dark:bg-white/10 flex items-center justify-center shrink-0 border border-border dark:border-white/10 group-hover:bg-emerald-500/20 group-hover:border-emerald-500/50 transition-all shadow-lg">
                  <span className="text-2xl">🚀</span>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2 text-foreground dark:text-white">{t('business_landing.benefit_3_title', 'Больше клиентов')}</h4>
                  <p className="text-muted-foreground dark:text-white/70 leading-relaxed text-sm">{t('business_landing.benefit_3_desc', 'Получайте готовые заявки из тендеров в 1 клик и растите бизнес.')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>

      <section className="py-16 md:py-24 container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12 md:mb-16 text-foreground">{t('business_landing.features_title')}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: Calendar, title: t('business_landing.feat_1_title'), desc: t('business_landing.feat_1_desc') },
            { icon: Users, title: t('business_landing.feat_2_title'), desc: t('business_landing.feat_2_desc') },
            { icon: ShieldCheck, title: t('business_landing.feat_3_title'), desc: t('business_landing.feat_3_desc') },
            { icon: ChartBar, title: t('business_landing.feat_4_title'), desc: t('business_landing.feat_4_desc') }
          ].map((feat, i) => (
            <div key={i} className="flex flex-col p-6 rounded-2xl border border-border bg-card hover:shadow-lg hover:border-primary/30 transition-all cursor-default">
              <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-6">
                <feat.icon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-card-foreground">{feat.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{feat.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Why join? (master offer) — the concrete reason to register ── */}
      <section className="container mx-auto px-4 pb-16 md:pb-24">
        <div className="relative overflow-hidden bg-linear-to-br from-primary/10 via-card to-indigo-500/10 border border-primary/20 rounded-3xl p-6 sm:p-10 md:p-12 shadow-xl">
          <div className="absolute top-[-60px] end-[-60px] w-[220px] h-[220px] bg-primary/20 blur-[90px] rounded-full" />
          <div className="relative z-10">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-black mb-3 text-foreground">{t('business_offer.title')}</h2>
              <p className="text-muted-foreground text-lg font-medium max-w-2xl mx-auto">{t('business_offer.subtitle')}</p>
            </div>

            {/* Trial highlights */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {isNative === false && (
                <span className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full font-black text-sm shadow-lg shadow-primary/25">
                  <Gift className="w-4 h-4" /> {t('business_offer.trial_badge', { months: trialMonths })}
                </span>
              )}
              {/* «Without a credit card» only makes sense next to the free
                  period it belongs to, and both are about paying — so in the
                  app the row keeps just the how-long-it-takes badge. */}
              {isNative === false && (
                <span className="inline-flex items-center gap-2 bg-card border border-border px-5 py-2.5 rounded-full font-bold text-sm text-foreground">
                  <CreditCard className="w-4 h-4 text-primary" /> {t('business_offer.no_card')}
                </span>
              )}
              <span className="inline-flex items-center gap-2 bg-card border border-border px-5 py-2.5 rounded-full font-bold text-sm text-foreground">
                <Timer className="w-4 h-4 text-primary" /> {t('business_offer.reg_time')}
              </span>
            </div>

            {/* Value list */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {OFFER_ITEMS.map(({ icon: Icon, n }) => (
                <div key={n} className="bg-card/80 backdrop-blur-sm border border-border rounded-2xl p-5 hover:border-primary/30 hover:shadow-lg transition-all">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-black text-base mb-1.5 text-foreground">{t(`business_offer.item_${n}_title`)}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t(`business_offer.item_${n}_desc`)}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col items-center gap-4">
              <Link href="/business/register"
                className="bg-primary text-primary-foreground px-10 py-4 rounded-full font-black text-lg hover:scale-105 transition-transform shadow-xl shadow-primary/25">
                {t('business_offer.cta')}
              </Link>
              {isNative === false && (
                <p className="text-xs text-muted-foreground text-center max-w-xl font-medium">{t('business_offer.price_note')}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Call to action Banner */}
      {whatsapp && (
        <section className="container mx-auto px-4 pb-10 text-center">
          {/* K-0049: главный канал связи для бизнеса — WhatsApp. */}
          <a href={waLink(whatsapp, 'שלום, אני בעל/ת עסק ומתעניין/ת ב-YofiPlace')}
             target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-8 py-4 text-lg font-black text-white shadow-lg hover:bg-emerald-600 transition-all">
            💬 דברו איתנו בוואטסאפ
          </a>
          <p className="mt-2 text-sm text-muted-foreground" dir="ltr">support@yofiplace.co.il</p>
        </section>
      )}

      <section className="container mx-auto px-4 pb-24">
        <div className="bg-linear-to-r from-primary/10 via-rose-500/10 to-indigo-500/10 border border-primary/20 rounded-3xl p-8 md:p-12 text-center flex flex-col items-center justify-center">
          <h2 className="text-3xl font-black mb-4">{t('business_landing.cta_title')}</h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl">
            {t('business_landing.cta_desc')}
          </p>
          <Link href="/business/register" className="bg-primary text-primary-foreground px-10 py-4 rounded-full font-bold text-lg hover:scale-105 transition-transform shadow-xl shadow-primary/20">
            {t('business_landing.cta_btn')}
          </Link>
        </div>
      </section>
    </div>
  );
}
